// Build 3D terrain geometry for one chunk from the domain data (GameState chunks +
// shared hex/terrain). Pure view: reads state, returns a BufferGeometry. No game
// rules, no mutation. Ground is the XZ plane, +Y is up. Owner: client-render3d.

import { BufferGeometry, BufferAttribute, Color } from 'three'
import { CHUNK, DIRS, chunkKey, hexToPixel, tileFromIdx } from '../../shared/hex'
import { buildingFootprint, treeWood } from '../../shared/constants'
import { Terrain } from '../../shared/terrain'
import { HEX_SIZE, type GameState } from '../state'
import { fogFade, fogGround, type FogState } from '../render/fog'
import { TERRAIN_COLOR, cliffColor, isWaterTerrain } from './palette'
import { rng3d } from './models3d/palette3d'

const S = HEX_SIZE
/** World-Y units a tile rises per elevation tier. */
export const H_PER_TIER = 8
const MAX_TIER = 8
/** How dark an explored-but-out-of-sight tile renders (the 3D shroud). */
const FOG_DIM = 0.52 // a faded-map wash, not a blackout — half the screen is memory
/** Land hex tops crown slightly at the centre, so the six facets catch the light
 *  and every tile reads as its own little plateau. Small enough that entities at
 *  the hex centre only sink toe-deep and overlays still clear the slope. */
export const DOME = 0.7
/** Skirt depth below a tile at the edge of the discovered world — the land reads
 *  as a thick floating slab instead of a paper-thin sheet against the void. */
const EDGE_DROP = 14

/** Stable per-hex hash → jitter that never changes frame to frame. */
function hexHash(q: number, r: number): number {
  return (Math.imul(q, 0x9e3779b1) ^ Math.imul(r, 0x85ebca77)) >>> 0
}

// ---- decoration palette (linear-space colours, lit by the shared Lambert) --------
const LEAF = [new Color('#4a9a40'), new Color('#3f8239'), new Color('#5fae4a'), new Color('#79b94e')]
const AUTUMN = [new Color('#cf7530'), new Color('#bd4c2e'), new Color('#d9a03c')]
const BLOSSOM = new Color('#e6e2e8')
const TRUNK = new Color('#6d5334')
const ROCK = new Color('#8b878f')
const ROCKDK = new Color('#6a6672') // shaded / lower rock, for tonal depth on massifs
const ROCKLT = new Color('#a7a4ac') // sun-caught upper rock
const HILLTOP = new Color('#8f9a68') // grassy hill crown (muted green over the tile tone)
const SNOWCAP = new Color('#eef2f6')
const SNOWTREE = new Color('#dfe7ec')
const TUFT = new Color('#8cc45e')
const GOLDTUFT = new Color('#b9c249')
const REED = new Color('#4a6238')
const SANDROCK = new Color('#c9b489')
const FOAM = new Color('#d8e8ec')
const SHALLOW = new Color('#4f92b8')
const ROAD = new Color('#8a7350')

// Pointy-top hexagon corner offsets in the ground plane (x,z), circumradius S.
export const CORNER: { x: number; z: number }[] = []
for (let i = 0; i < 6; i++) {
  const a = (Math.PI / 180) * (60 * i - 90)
  CORNER.push({ x: S * Math.cos(a), z: S * Math.sin(a) })
}
// For each hex edge (corner i → i+1), which neighbour DIRS index it faces — matched
// by comparing the edge's outward direction to each neighbour's pixel direction.
export const EDGE_DIR: number[] = (() => {
  const dirAng = DIRS.map((d) => {
    const p = hexToPixel(d.q, d.r, 1)
    return Math.atan2(p.y, p.x)
  })
  const out: number[] = []
  for (let i = 0; i < 6; i++) {
    const em = (Math.PI / 180) * (60 * i - 60) // edge-midpoint direction
    let best = 0, bestD = -Infinity
    for (let d = 0; d < 6; d++) {
      const dot = Math.cos(em - dirAng[d])
      if (dot > bestD) { bestD = dot; best = d }
    }
    out.push(best)
  }
  return out
})()

function localIdx(q: number, r: number): number {
  const lq = ((q % CHUNK) + CHUNK) % CHUNK
  const lr = ((r % CHUNK) + CHUNK) % CHUNK
  return lr * CHUNK + lq
}
/** Render tier of a hex across chunks (water rides sea level 0), or undefined when
 *  the chunk is unloaded / carries no elevation — mirrors the 2.5D renderTier. */
function tierAt(st: GameState, q: number, r: number): number | undefined {
  const c = st.chunks.get(chunkKey(Math.floor(q / CHUNK), Math.floor(r / CHUNK)))
  if (!c || !c.elev) return undefined
  const i = localIdx(q, r)
  return isWaterTerrain(c.tiles[i] as Terrain) ? 0 : c.elev[i]
}

/** World-Y of a hex's top surface (0 when unloaded / flat) — used to sit entities
 *  on the ground. Mirrors the mesh's tier→height mapping. */
export function tileHeight(st: GameState, q: number, r: number): number {
  return (tierAt(st, q, r) ?? 0) * H_PER_TIER
}

/** Faction that owns a hex (undefined = neutral / unloaded). */
export function ownerAt(st: GameState, q: number, r: number): string | undefined {
  const c = st.chunks.get(chunkKey(Math.floor(q / CHUNK), Math.floor(r / CHUNK)))
  return c ? c.owners.get(localIdx(q, r)) : undefined
}

/** Non-indexed geometry (flat-shaded) for a chunk: a hex top per tile plus cliff
 *  walls on every downhill edge. Positions in world units; per-vertex colours.
 *  Tiles out of current vision (fog) are darkened to an "explored memory" shroud. */
export function buildChunkGeometry(st: GameState, cx: number, cy: number, fog: FogState | null): BufferGeometry {
  const ck = st.chunks.get(chunkKey(cx, cy))!
  // Hexes covered by a building footprint skip their baked decorations — a lone
  // tree or boulder must not poke through a farm field or house floor. (Placing
  // a building claims territory, which bumps the chunk version and re-bakes.)
  const boccupied = new Set<number>()
  for (const b of st.buildings.values()) {
    // A chop order is a felling MARKER, not a structure — the tree must keep standing
    // on its hex until the fell finishes (then the tile turns to grass and re-bakes).
    if (b.type === 'chop' || b.type === 'plant') continue
    for (const c of buildingFootprint(b.type, b.q, b.r, b.dir)) {
      if (Math.floor(c.q / CHUNK) === cx && Math.floor(c.r / CHUNK) === cy) boccupied.add(localIdx(c.q, c.r))
    }
  }
  const pos: number[] = []
  const col: number[] = []
  const hx: number[] = [] // per-vertex (q,r) so picking maps a triangle → its hex
  let curQ = 0, curR = 0

  const pushTri = (
    ax: number, ay: number, az: number, bx: number, by: number, bz: number,
    dx: number, dy: number, dz: number, r: number, g: number, b: number,
  ): void => {
    // The terrain builders describe faces clockwise from outside. Reverse them
    // so normals point out of the surface: DoubleSide can hide inward normals
    // in the colour pass, but shadow normal bias still uses the original normal
    // and would otherwise push ground samples below their own shadow caster.
    pos.push(ax, ay, az, dx, dy, dz, bx, by, bz)
    col.push(r, g, b, r, g, b, r, g, b)
    hx.push(curQ, curR, curQ, curR, curQ, curR)
  }

  // Cross-chunk lookups for shorelines and road spokes.
  const terrAt = (qq: number, rr: number): Terrain | undefined => {
    const c = st.chunks.get(chunkKey(Math.floor(qq / CHUNK), Math.floor(rr / CHUNK)))
    return c ? (c.tiles[localIdx(qq, rr)] as Terrain) : undefined
  }
  const roadAt = (qq: number, rr: number): boolean => {
    const c = st.chunks.get(chunkKey(Math.floor(qq / CHUNK), Math.floor(rr / CHUNK)))
    return !!c && c.roads.has(localIdx(qq, rr))
  }

  const PI2 = Math.PI * 2
  /** Faceted cone (flip = hanging downward) — the whole decoration kit builds on it. */
  const cone = (
    x: number, yb: number, z: number, rad: number, h: number, sides: number,
    c: Color, k: number, phase = 0, flip = false,
  ): void => {
    for (let e = 0; e < sides; e++) {
      const a0 = phase + (e / sides) * PI2, a1 = phase + ((e + 1) / sides) * PI2
      const fs = (0.88 + (e % 3) * 0.08) * k
      const x0 = x + Math.cos(a0) * rad, z0 = z + Math.sin(a0) * rad
      const x1 = x + Math.cos(a1) * rad, z1 = z + Math.sin(a1) * rad
      if (flip) pushTri(x, yb - h, z, x1, yb, z1, x0, yb, z0, c.r * fs * 0.72, c.g * fs * 0.72, c.b * fs * 0.72)
      else pushTri(x, yb + h, z, x0, yb, z0, x1, yb, z1, c.r * fs, c.g * fs, c.b * fs)
    }
  }
  /** Soft baked "shadow" disc that grounds a decoration (darkened tile colour). */
  const disc = (x: number, yb: number, z: number, rad: number, r: number, g: number, b: number): void => {
    for (let e = 0; e < 6; e++) {
      const a0 = (e / 6) * PI2, a1 = ((e + 1) / 6) * PI2
      pushTri(x, yb, z, x + Math.cos(a0) * rad, yb, z + Math.sin(a0) * rad,
        x + Math.cos(a1) * rad, yb, z + Math.sin(a1) * rad, r, g, b)
    }
  }
  /** Open 3-sided trunk prism (caps hidden by canopy/ground). */
  const trunk = (x: number, yb: number, z: number, rad: number, h: number, k: number): void => {
    for (let e = 0; e < 3; e++) {
      const a0 = (e / 3) * PI2, a1 = ((e + 1) / 3) * PI2
      const fs = (0.84 + e * 0.1) * k
      const x0 = x + Math.cos(a0) * rad, z0 = z + Math.sin(a0) * rad
      const x1 = x + Math.cos(a1) * rad, z1 = z + Math.sin(a1) * rad
      pushTri(x0, yb + h, z0, x0, yb, z0, x1, yb, z1, TRUNK.r * fs, TRUNK.g * fs, TRUNK.b * fs)
      pushTri(x0, yb + h, z0, x1, yb, z1, x1, yb + h, z1, TRUNK.r * fs, TRUNK.g * fs, TRUNK.b * fs)
    }
  }

  for (let i = 0; i < CHUNK * CHUNK; i++) {
    const { q, r } = tileFromIdx(cx, cy, i)
    curQ = q; curR = r
    // Never-seen ground is true void: no top, no walls, nothing to give it away.
    if (fog && !fogGround(fog, q, r)) continue
    const t = ck.tiles[i] as Terrain
    const water = isWaterTerrain(t)
    const tier = tierAt(st, q, r) ?? 0
    const p = hexToPixel(q, r, S)
    const y = tier * H_PER_TIER
    const hh = hexHash(q, r)
    // stable per-hex jitter (slot 0..5 picks independent bits, amt = full swing)
    const jig = (slot: number, amt: number): number =>
      1 + ((((hh >>> (slot * 5)) & 31) / 31) - 0.5) * amt
    // top colour: brightened with height, jittered per hex so each tile reads as its
    // own patch (a touch of independent green drift keeps the jitter from feeling like
    // plain brightness banding); calm water stays uniform. Fogged tiles dim.
    const base = TERRAIN_COLOR[t]
    const dim = 1 // fog shading happens in one post-pass over the baked colors
    const jl = water ? 1 : jig(0, 0.10)
    const jg = water ? 1 : jig(1, 0.06)
    const sh = (1 + Math.max(-MAX_TIER, Math.min(MAX_TIER, tier)) * 0.045) * dim * jl
    let tr = base.r * sh, tg = base.g * sh * jg, tb = base.b * sh
    // shoreline: water beside land brightens to shallows and gets a foam rim
    const landEdge: boolean[] = [false, false, false, false, false, false]
    let shore = false
    if (water) {
      for (let e = 0; e < 6; e++) {
        const d = DIRS[EDGE_DIR[e]]
        const nt2 = terrAt(q + d.q, r + d.r)
        if (nt2 !== undefined && !isWaterTerrain(nt2)) { landEdge[e] = true; shore = true }
      }
      if (shore) {
        tr = tr + (SHALLOW.r * dim - tr) * 0.4
        tg = tg + (SHALLOW.g * dim - tg) * 0.4
        tb = tb + (SHALLOW.b * dim - tb) * 0.4
      }
    }
    // top face: 6 tris fanned from a slightly crowned centre; each facet gets its own
    // faint shade so the low-poly pie-slices read even under flat noon light
    const crownY = y + (water ? 0 : DOME)
    for (let e = 0; e < 6; e++) {
      const c0 = CORNER[e], c1 = CORNER[(e + 1) % 6]
      const fs = water ? 1 : 1 + ((((hh >>> (e * 4)) & 15) / 15) - 0.5) * 0.05
      pushTri(
        p.x, crownY, p.y,
        p.x + c0.x, y, p.y + c0.z,
        p.x + c1.x, y, p.y + c1.z,
        tr * fs, tg * fs, tb * fs,
      )
    }
    if (water) {
      // thin foam band hugging each land-facing edge
      for (let e = 0; e < 6; e++) {
        if (!landEdge[e]) continue
        const c0 = CORNER[e], c1 = CORNER[(e + 1) % 6]
        const fy = y + 0.22
        pushTri(
          p.x + c0.x, fy, p.y + c0.z, p.x + c1.x, fy, p.y + c1.z,
          p.x + c1.x * 0.86, fy, p.y + c1.z * 0.86,
          FOAM.r * dim, FOAM.g * dim, FOAM.b * dim,
        )
        pushTri(
          p.x + c0.x, fy, p.y + c0.z, p.x + c1.x * 0.86, fy, p.y + c1.z * 0.86,
          p.x + c0.x * 0.86, fy, p.y + c0.z * 0.86,
          FOAM.r * dim, FOAM.g * dim, FOAM.b * dim,
        )
      }
      continue // flat sea drops no walls, no decorations
    }
    // soil walls: down every downhill edge, and a deep skirt where the world ends
    // (unloaded neighbour), so the discovered land reads as a thick slab
    const cc = cliffColor(base)
    const jw = jig(2, 0.10)
    const cr = cc.r * dim * jw, cg = cc.g * dim * jw, cb = cc.b * dim * jw
    for (let e = 0; e < 6; e++) {
      const d = DIRS[EDGE_DIR[e]]
      const unseen = fog !== null && !fogGround(fog, q + d.q, r + d.r)
      const nt = unseen ? undefined : tierAt(st, q + d.q, r + d.r)
      if (nt !== undefined && nt >= tier) continue
      const ny = nt === undefined ? y - EDGE_DROP : nt * H_PER_TIER
      const c0 = CORNER[e], c1 = CORNER[(e + 1) % 6]
      const x0 = p.x + c0.x, z0 = p.y + c0.z
      const x1 = p.x + c1.x, z1 = p.y + c1.z
      // shade the wall a touch darker toward its base
      pushTri(x0, y, z0, x1, ny, z1, x1, y, z1, cr, cg, cb)
      pushTri(x0, y, z0, x0, ny, z0, x1, ny, z1, cr * 0.78, cg * 0.78, cb * 0.78)
    }

    // ---- roads & decorations (baked into the chunk: one draw call, free picking) --
    const rng = rng3d(hh)
    /** Ground height on the crowned top at fraction fr (0=centre..1=corner). */
    const gnd = (fr: number): number => y + DOME * Math.max(0, 1 - fr)
    if (boccupied.has(i)) {
      // building above: bare ground only
    } else if (ck.roads.has(i)) {
      // worn dirt: a centre patch + a spoke toward every neighbouring road hex
      const ry = 0.18
      for (let e = 0; e < 6; e++) {
        const c0 = CORNER[e], c1 = CORNER[(e + 1) % 6]
        pushTri(
          p.x, crownY + ry, p.y,
          p.x + c0.x * 0.42, gnd(0.42) + ry, p.y + c0.z * 0.42,
          p.x + c1.x * 0.42, gnd(0.42) + ry, p.y + c1.z * 0.42,
          ROAD.r * dim, ROAD.g * dim, ROAD.b * dim,
        )
      }
      for (let e = 0; e < 6; e++) {
        const d = DIRS[EDGE_DIR[e]]
        if (!roadAt(q + d.q, r + d.r)) continue
        const c0 = CORNER[e], c1 = CORNER[(e + 1) % 6]
        const mx = (c0.x + c1.x) / 2, mz = (c0.z + c1.z) / 2
        const ml = Math.hypot(mx, mz) || 1
        const wx = (-mz / ml) * 3.2, wz = (mx / ml) * 3.2 // spoke half-width
        const k = 0.94 * dim
        pushTri(p.x + wx, crownY + ry, p.y + wz, p.x - wx, crownY + ry, p.y - wz,
          p.x + mx - wx, y + ry, p.y + mz - wz, ROAD.r * k, ROAD.g * k, ROAD.b * k)
        pushTri(p.x + wx, crownY + ry, p.y + wz, p.x + mx - wx, y + ry, p.y + mz - wz,
          p.x + mx + wx, y + ry, p.y + mz + wz, ROAD.r * k, ROAD.g * k, ROAD.b * k)
      }
    } else if (ck.trees && ck.trees[i] === 1) {
      // A standing tree(s) on ordinary grassland — woodland is no longer a biome, just a
      // per-hex flag over grass. The hex grows a small STAND: 1–3 trees, denser where the
      // hex holds more wood, each scattered across the whole crown (not stacked in the
      // centre) at its own size, species and tint so a wood never reads as a tidy grid.
      // The whole stand keeps standing through a chop order and vanishes only when the
      // fell clears the flag. Baked into the chunk like every other decoration.
      const wood = treeWood(q, r)
      const dense = wood / 100 // 0.1 (sapling hex) … 1 (ancient stand)
      let n = 1
      if (rng() < 0.45 + dense * 0.35) n++
      if (rng() < 0.15 + dense * 0.35) n++ // → 1..3, more where the wood is richer
      const baseS = (1.5 + dense * 1.4) / (1 + (n - 1) * 0.22) // crowded stands run smaller
      const spin = rng() * PI2 // rotate the whole ring so stands don't share an orientation
      for (let k = 0; k < n; k++) {
        // one tree near-centre when solo; spread the rest around the crown, angularly
        // separated so they don't overlap into one blob.
        const ang = spin + (k / n) * PI2 + rng() * 1.4
        const fr = n === 1 ? rng() * 0.3 : 0.2 + rng() * 0.36
        const x = p.x + Math.cos(ang) * fr * S, z = p.y + Math.sin(ang) * fr * S
        const gy = gnd(fr) - 0.35
        const s = baseS * (0.78 + rng() * 0.5) // per-tree size jitter
        const roll = rng()
        const c = roll < 0.12 ? AUTUMN[(rng() * 3) | 0] : roll < 0.16 ? BLOSSOM : LEAF[(rng() * 4) | 0]
        disc(x, gy + 0.45, z, 3.4 * s, tr * 0.7, tg * 0.7, tb * 0.7)
        if (rng() < 0.45) { // conifer: two stacked cones
          cone(x, gy + 0.3, z, 3.0 * s, 6.8 * s, 6, c, dim, rng() * PI2)
          cone(x, gy + 4.6 * s, z, 2.1 * s, 5.6 * s, 6, c, dim * 1.08, rng() * PI2)
        } else { // broadleaf: trunk + faceted blob (bipyramid)
          trunk(x, gy, z, 0.55 * s, 4.4 * s, dim)
          const ring = gy + 5.6 * s
          cone(x, ring, z, 3.1 * s, 4.0 * s, 5, c, dim, rng() * PI2)
          cone(x, ring, z, 3.1 * s, 2.3 * s, 5, c, dim, rng() * PI2, true)
        }
      }
    } else {
      // scenery per terrain — deterministic per hex, dimmed under fog like the ground.
      // Tall pieces only grow on unbuildable ground (forest/mountain/swamp) so nothing
      // ever pokes through a building; grass gets ankle-high tufts at most.
      const put = (): { x: number; z: number; g: number } => {
        const ang = rng() * PI2, fr = 0.2 + rng() * 0.42
        return { x: p.x + Math.cos(ang) * fr * S, z: p.y + Math.sin(ang) * fr * S, g: gnd(fr) - 0.25 }
      }
      switch (t) {
        case Terrain.Forest: {
          // Big overlapping canopies, TALLER than a serf by far — the forest reads as
          // real woodland. Baked shadow discs ground each tree; autumn/blossom pops.
          const n = 2 + (rng() < 0.55 ? 1 : 0)
          for (let k = 0; k < n; k++) {
            const ang = (k / n) * PI2 + rng() * 1.6
            const fr = 0.14 + rng() * 0.24
            const x = p.x + Math.cos(ang) * fr * S, z = p.y + Math.sin(ang) * fr * S
            const gy = gnd(fr) - 0.35
            const s = 2.1 + rng() * 1.2 // canopy radius ≈ 6–10, heights ≈ 22–36
            const roll = rng()
            const c = roll < 0.13 ? AUTUMN[(rng() * 3) | 0] : roll < 0.18 ? BLOSSOM : LEAF[(rng() * 4) | 0]
            disc(x, gy + 0.45, z, 3.4 * s, tr * 0.72, tg * 0.72, tb * 0.72)
            if (rng() < 0.5) { // conifer: two stacked cones
              cone(x, gy + 0.3, z, 3.0 * s, 6.8 * s, 6, c, dim, rng() * PI2)
              cone(x, gy + 4.6 * s, z, 2.1 * s, 5.6 * s, 6, c, dim * 1.08, rng() * PI2)
            } else { // broadleaf: trunk + faceted blob (bipyramid)
              trunk(x, gy, z, 0.55 * s, 4.4 * s, dim)
              const ring = gy + 5.6 * s
              cone(x, ring, z, 3.1 * s, 4.0 * s, 5, c, dim, rng() * PI2)
              cone(x, ring, z, 3.1 * s, 2.3 * s, 5, c, dim, rng() * PI2, true)
            }
          }
          break
        }
        case Terrain.Mountain: {
          // A craggy massif that fills the hex: a broad rocky base stacked into a main
          // peak with an offset secondary crag, snow on the tops of the tall ranges.
          // Wide base + tiered build-up reads as a mountain, not a needle.
          const ph = rng() * PI2
          const H = 24 + tier * 5.5 + rng() * 6 // total peak height above the hex top
          const base = S * 0.98 // fills the hex footprint
          // broad lower massif — two overlapping wide cones, offset, for a rugged base
          cone(p.x, y - 0.6, p.y, base, H * 0.5, 7, ROCKDK, dim * 0.9, ph)
          cone(p.x - base * 0.16, y - 0.4, p.y + base * 0.1, base * 0.74, H * 0.66, 6, ROCK, dim * 0.95, ph + 1.2)
          // the main peak rising out of the massif (wide enough to avoid a spire)
          const px = p.x + base * 0.06, pz = p.y - base * 0.05
          cone(px, y - 0.4 + H * 0.26, pz, base * 0.56, H * 0.6, 6, ROCK, dim, ph + 0.5)
          cone(px, y - 0.4 + H * 0.55, pz, base * 0.34, H * 0.36, 6, ROCKLT, dim * 1.04, ph + 0.5)
          // an offset secondary crag for asymmetry
          const a2 = rng() * PI2, off = base * 0.44
          const cx = p.x + Math.cos(a2) * off, cz = p.y + Math.sin(a2) * off
          cone(cx, y - 0.3, cz, base * 0.44, H * 0.52, 5, ROCK, dim * 0.88, a2 + 2)
          // snow on the peaks of the tall ranges
          if (tier >= 5) {
            cone(px, y - 0.4 + H * 0.62, pz, base * 0.3, H * 0.32, 6, SNOWCAP, dim * 1.08, ph + 0.5)
            cone(cx, y - 0.3 + H * 0.32, cz, base * 0.22, H * 0.22, 5, SNOWCAP, dim * 1.05, a2 + 2)
          }
          // scree at the foot
          for (let k = 0; k < 2; k++) { const o = put(); cone(o.x, o.g, o.z, 3 + rng() * 2, 2.4 + rng() * 2, 4, ROCKDK, dim * 0.82, rng() * PI2) }
          break
        }
        case Terrain.Hills: {
          // A rolling grassy hill: a broad low crown domes the raised hex so it reads as
          // a mound (not a flat mesa), with a rock outcrop or two and a tuft of grass.
          const cr = S * 0.94
          const crown = 4 + tier * 1.1 + rng() * 2
          disc(p.x, y + 0.15, p.y, cr, tr * 0.82, tg * 0.82, tb * 0.82)
          cone(p.x, y - 0.3, p.y, cr, crown, 7, HILLTOP, dim * 0.96, rng() * PI2)
          cone(p.x, y - 0.3 + crown * 0.5, p.y, cr * 0.6, crown * 0.7, 6, HILLTOP, dim, rng() * PI2)
          const rn = 1 + ((rng() * 2) | 0)
          for (let k = 0; k < rn; k++) { const o = put(); cone(o.x, o.g + crown * 0.3, o.z, 2.4 + rng() * 1.6, 2.6 + rng() * 2, 5, ROCK, dim * 0.9, rng() * PI2) }
          const o2 = put(); cone(o2.x, o2.g + crown * 0.4, o2.z, 0.7, 2.4, 4, TUFT, dim)
          break
        }
        case Terrain.Swamp: {
          const n = 4 + ((rng() * 2) | 0)
          for (let k = 0; k < n; k++) { const o = put(); cone(o.x, o.g, o.z, 0.7, 3.8 + rng() * 2.4, 4, REED, dim) }
          if (rng() < 0.4) { // a squat marsh shrub
            const o = put()
            cone(o.x, o.g + 1.4, o.z, 2.6, 2.2, 5, REED, dim * 1.15, rng() * PI2)
            cone(o.x, o.g + 1.4, o.z, 2.6, 1.4, 5, REED, dim * 1.15, rng() * PI2, true)
          }
          break
        }
        case Terrain.Grass: {
          // Only ground tufts — a standing tree on grass is ALWAYS a real, choppable tree
          // (the per-hex `trees` layer, handled above), never scenery, so the world never
          // shows a tree you can't fell. (Matches the 2.5D renderer's grass: blades only.)
          if (rng() < 0.55) {
            const n2 = 2 + ((rng() * 3) | 0)
            for (let k = 0; k < n2; k++) { const o = put(); cone(o.x, o.g, o.z, 0.7, 2 + rng(), 4, TUFT, dim) }
          }
          break
        }
        case Terrain.Fertile: {
          if (rng() < 0.5) { for (let k = 0; k < 4; k++) { const o = put(); cone(o.x, o.g, o.z, 0.7, 1.8 + rng(), 4, GOLDTUFT, dim) } }
          break
        }
        case Terrain.Sand: {
          if (rng() < 0.14) { const o = put(); cone(o.x, o.g, o.z, 2 + rng() * 1.2, 1.6 + rng() * 1.2, 4, SANDROCK, dim, rng() * PI2) }
          break
        }
        case Terrain.Snow: {
          if (rng() < 0.3) {
            const o = put(); const s = 1.9 + rng() * 1.0
            disc(o.x, o.g + 0.4, o.z, 3.2 * s, tr * 0.8, tg * 0.8, tb * 0.8)
            cone(o.x, o.g + 0.3, o.z, 3.0 * s, 6.4 * s, 6, SNOWTREE, dim, rng() * PI2)
            cone(o.x, o.g + 4.3 * s, o.z, 2.1 * s, 5.2 * s, 6, SNOWTREE, dim * 1.05, rng() * PI2)
          }
          break
        }
      }
    }
  }

  // Fog grade: remembered ground fades toward a cool, desaturated slate, easing in
  // across the feather band past the lit bubble. One pass over the baked colors so
  // every feature — tops, cliffs, roads, trees — greys out together.
  if (fog) {
    let lastQ = NaN, lastR = NaN, f = 1
    for (let v = 0, n = hx.length >> 1; v < n; v++) {
      const vq = hx[v * 2], vr = hx[v * 2 + 1]
      if (vq !== lastQ || vr !== lastR) { lastQ = vq; lastR = vr; f = fogFade(fog, vq, vr, FOG_DIM) }
      if (f >= 1) continue
      const m = (1 - f) / (1 - FOG_DIM) // 0 at the lit edge → 1 deep in memory
      const i = v * 3
      const l = col[i] * 0.32 + col[i + 1] * 0.52 + col[i + 2] * 0.16 // luma
      const mix = 0.88 * m
      col[i] += (l * 0.50 + 0.018 - col[i]) * mix
      col[i + 1] += (l * 0.56 + 0.030 - col[i + 1]) * mix
      col[i + 2] += (l * 0.72 + 0.055 - col[i + 2]) * mix
    }
  }

  const g = new BufferGeometry()
  g.setAttribute('position', new BufferAttribute(new Float32Array(pos), 3))
  g.setAttribute('color', new BufferAttribute(new Float32Array(col), 3))
  g.setAttribute('hex', new BufferAttribute(new Float32Array(hx), 2))
  g.computeVertexNormals()
  return g
}
